import {
  useGetEnrollmentJointDocumentVerificationData,
  useGetEnrollmentJointDocumentVerificationDataV2,
  useSubmitEnrollmentJointDocumentVerificationData,
  useSubmitEnrollmentJointDocumentVerificationDataV2,
  useSubmitEnrollmentJointDocVerificationCheckStatusData,
  useSubmitEnrollmentJointDocVerificationCheckStatusDataV2,
} from '@egw/api'
import { DocumentVerificationStatus } from '@egw/api/src/models/enrollment/enrollment'
import { Alert, AlertType, Button, GenericError, Tag } from '@egw/components'
import { sendEvent } from '@egw/events'
import { UserIcon } from '@heroicons/react/16/solid'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useRef, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import {
  BackId,
  FaceMatch,
  FrontId,
  ProcessId,
  RetrySteps,
  Selfie,
  ResetPermissions,
  usePermissions,
  CameraPermissionState,
  ConsentScreen,
  MobileRedirect,
} from '@egw/features-auth'
import { getIncodeClient } from '@egw/risk-service'
import cx from 'classnames'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Joint - DocumentVerification')

const INTERVAL_TO_POLL_SUBMIT_ENROLLMENT = 5000

export const DocumentVerificationPage: FC = () => {
  const { flags } = useFlags<FeatureFlags>()

  return flags.rolloutIncodeSdkDocumentVerificationEnrollment ? <DocumentVerificationV2 /> : <DocumentVerification />
}

export const DocumentVerification: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, error } = useGetEnrollmentJointDocumentVerificationData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointDocumentVerificationData()
  const { mutate: checkStatus, error: checkStatusError } = useSubmitEnrollmentJointDocVerificationCheckStatusData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint document verification data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint document verification form', submitError)
    }
  }, [submitError])

  // Log check status errors
  useEffect(() => {
    if (checkStatusError) {
      log.error('Error checking document verification status', checkStatusError)
    }
  }, [checkStatusError])

  const [isPending, setIsPending] = useState(false)
  const [verifyDocumentUrl, setVerifyDocumentUrl] = useState<string>()

  const applicationId = data && !data.isError ? data.activePerson.applicationId : ''
  const profileName = data?.activePerson.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  // Protect route
  useValidateRoute(data?.activePerson.path)

  const onSubmit = async (): Promise<void> => {
    try {
      const response = await mutate({
        activePerson: {
          triggerDocumentVerification: true,
        },
      })

      const docStatus = response?.activePerson.documentVerificationStatus
      const docUrl = response?.activePerson.documentVerificationUrl

      if (docStatus === DocumentVerificationStatus.Pending) {
        setIsPending(true)
        setVerifyDocumentUrl(docUrl)
        window.open(docUrl, '_blank')
      } else {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onPollSubmit = async (): Promise<void> => {
    try {
      const response = await checkStatus({})

      // Check for API errors in the response
      if (response?.isError && response.errors.length) {
        const _errors = response.errors
        log.error('Document verification check status errors:', response.errors)

        if (_errors.includes('APPLICANT_IN_MANUAL_REVIEW')) {
          return void navigate(
            `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.UNDER_REVIEW.path}`,
          )
        } else if (_errors.includes('USER_UNABLE_TO_ENROLL')) {
          return void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.DECLINE.path}`)
        }
        return void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }

      if (
        !response?.isError &&
        response?.activePerson.documentVerificationStatus === DocumentVerificationStatus.Complete
      ) {
        setIsPending(false)

        void navigate(
          `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.JOINT.ROUTES.DOCUMENT_VERIFICATION_COMPLETE.path}`,
        )
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  useEffect(() => {
    if (applicationId) {
      sendEvent('bankjoint_enroll_docV_started', {
        applicationId,
      })
    }
  }, [applicationId])

  useEffect(() => {
    if (!isPending) {
      return
    }

    intervalRef.current = setInterval(() => {
      void onPollSubmit()
    }, INTERVAL_TO_POLL_SUBMIT_ENROLLMENT)

    return () => {
      const intervalId = intervalRef.current
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPending])

  return (
    <EnrollmentJointLayout title={t('joint.doc-verification.page_title')}>
      {profileName ? (
        <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
          {profileName}
        </Tag>
      ) : null}
      <PageHeader className="mb-xl" title={t('joint.doc-verification.title')} />
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div>
          {!isPending ? (
            <>
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.why_required_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.why_required_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.is_info_secure_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.is_info_secure_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-sm">
                {t('joint.doc-verification.how_verification_work')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.photograph_id_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-sm">
                {t('joint.doc-verification.photograph_id_description')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.take_a_selfie')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.take_a_selfie_description')}
              </p>
            </>
          ) : null}

          {isPending ? (
            <>
              <p className="typo-primary-base-regular text-text-meta mb-xl">{t('joint.doc-verification.thank_you')}</p>
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.complete_verification')}
              </p>
              <a
                href={verifyDocumentUrl}
                target="_blank"
                className="typo-primary-base-regular text-text-action mb-xl"
                rel="noopener"
              >
                {verifyDocumentUrl}
              </a>
              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.complete_verification_process')}
              </p>
            </>
          ) : null}

          <div className="flex-1 md:hidden" />

          {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isLoading={isSubmitting || isPending}
            onClick={() => void onSubmit()}
            testId="primaryAction_button"
          >
            {isPending
              ? t('joint.doc-verification.verification_in_progress')
              : t('joint.doc-verification.start_verification')}
          </Button>
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? checkStatusError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}

const saveDeviceData = (session: { token: string }): void => {
  void getIncodeClient().sendGeolocation({ token: session.token })
  void getIncodeClient().sendFingerprint({ token: session.token })
}

export const DocumentVerificationV2: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, error } = useGetEnrollmentJointDocumentVerificationDataV2()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointDocumentVerificationDataV2()
  const { mutate: checkStatus, error: checkStatusError } = useSubmitEnrollmentJointDocVerificationCheckStatusDataV2()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint document verification data V2', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint document verification form V2', submitError)
    }
  }, [submitError])

  // Log check status errors
  useEffect(() => {
    if (checkStatusError) {
      log.error('Error checking document verification status V2', checkStatusError)
    }
  }, [checkStatusError])

  const [isShowDocV, setIsShowDocV] = useState(false)

  const permissionsState = usePermissions()

  const [incodeSession, setIncodeSession] = useState<{ token: string } | undefined>(undefined)
  const [isIncodeError, setIsIncodeError] = useState(false)
  const [step, setStep] = useState(0)
  const [resetPermissions, setResetPermissions] = useState(false)
  const [liveness, setLiveness] = useState(false)
  const [userExists, setUserExists] = useState(false)

  const [isPending, setIsPending] = useState(false)
  const [verifyId, setVerifyId] = useState<string>()

  const applicationId = data && !data.isError ? data.activePerson?.applicationId : ''
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  const onSubmit = async (): Promise<void> => {
    try {
      const response = await mutate({
        activePerson: {
          triggerDocumentVerification: true,
        },
      })

      // Check for API errors in the response
      if (response?.isError && response.errors.length) {
        log.error('Document verification submit errors:', response.errors)
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        return
      }

      const docStatus = response?.activePerson.documentVerificationStatus
      const documentVerificationId = response?.activePerson.documentVerificationId

      if (docStatus === DocumentVerificationStatus.Pending) {
        setVerifyId(documentVerificationId)
        setIsShowDocV(true)
        setIsPending(true)
      } else {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onPollSubmit = async (): Promise<void> => {
    try {
      const response = await checkStatus({})

      // Check for API errors in the response
      if (response?.isError && response.errors.length) {
        const _errors = response.errors
        log.error('Document verification check status errors:', response.errors)

        if (_errors.includes('APPLICANT_IN_MANUAL_REVIEW')) {
          return void navigate(
            `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.UNDER_REVIEW.path}`,
          )
        } else if (_errors.includes('USER_UNABLE_TO_ENROLL')) {
          return void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.DECLINE.path}`)
        }
        return void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }

      if (
        !response?.isError &&
        response?.activePerson.documentVerificationStatus === DocumentVerificationStatus.Complete
      ) {
        setIsPending(false)

        void navigate(
          `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.JOINT.ROUTES.DOCUMENT_VERIFICATION_COMPLETE.path}`,
        )
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  useEffect(() => {
    if (applicationId) {
      sendEvent('bankjoint_enroll_docV_started', {
        applicationId,
      })
    }
  }, [applicationId])

  useEffect(() => {
    if (!isPending) {
      return
    }

    intervalRef.current = setInterval(() => {
      void onPollSubmit()
    }, INTERVAL_TO_POLL_SUBMIT_ENROLLMENT)

    return () => {
      const intervalId = intervalRef.current
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPending])

  useEffect(() => {
    if (!verifyId) {
      return
    }

    void getIncodeClient()
      .createSession('US', verifyId)
      .then(async (session) => {
        await getIncodeClient().warmup()
        setIncodeSession(session)

        saveDeviceData(session)
      })
      .catch((e) => {
        log.error('Error creating Incode session', e)
        setIncodeSession(undefined)
        setIsIncodeError(true)
      })
  }, [verifyId])

  const goNext = (): void => {
    setStep(step + 1)
  }

  useEffect(() => {
    // if permissions are denied from start, let's show the reset permissions screen
    setResetPermissions(permissionsState === CameraPermissionState.DENIED)
  }, [permissionsState])

  const handleError = (e?: { type: string }): void => {
    if (e?.type === 'permissionDenied') {
      setResetPermissions(true)
    }
  }

  if (resetPermissions) {
    return (
      <ResetPermissions
        onTryAgain={() => {
          setResetPermissions(false)
        }}
      />
    )
  }

  return (
    <EnrollmentJointLayout
      title={t('joint.doc-verification.page_title')}
      className={cx({
        '!max-w-full': isShowDocV && incodeSession,
      })}
    >
      {!incodeSession ? (
        <>
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader className="mb-xl" title={t('joint.doc-verification.title')} />
        </>
      ) : null}

      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-10" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          {!isShowDocV || !incodeSession ? (
            <>
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.why_required_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.why_required_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.is_info_secure_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.is_info_secure_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-sm">
                {t('joint.doc-verification.how_verification_work')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.photograph_id_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-sm">
                {t('joint.doc-verification.photograph_id_description')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('joint.doc-verification.take_a_selfie')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('joint.doc-verification.take_a_selfie_description')}
              </p>

              <div className="flex-1 md:hidden" />

              {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

              {/* AUTO GENERATED COMPONENTS */}

              <Button
                type="submit"
                className="w-full md:w-min mt-xl"
                isLoading={isSubmitting || isPending}
                onClick={() => void onSubmit()}
                testId="primaryAction_button"
              >
                {isPending
                  ? t('joint.doc-verification.verification_in_progress')
                  : t('joint.doc-verification.start_verification')}
              </Button>
            </>
          ) : null}

          {isShowDocV && incodeSession ? (
            <>
              {
                [
                  !getIncodeClient().isDesktop() && (
                    <ConsentScreen key="consent" session={incodeSession} onAccept={goNext} />
                  ),
                  <MobileRedirect
                    key={`mobile-redirect-${step.toString()}`}
                    session={incodeSession}
                    flowId={verifyId ?? ''}
                    onSuccess={() => {
                      if (getIncodeClient().isDesktop()) {
                        setIsShowDocV(false)
                      } else {
                        // if mobile, skip to selfie directly
                        goNext()
                      }
                    }}
                    onError={handleError}
                  />,
                  <FrontId
                    key={`front-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={goNext}
                    onError={handleError}
                  />,
                  <BackId
                    key={`back-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={goNext}
                    onError={handleError}
                  />,
                  <ProcessId key={`process-${step.toString()}`} session={incodeSession} onSuccess={goNext} />,
                  <Selfie
                    key={`selfie-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={(res) => {
                      setLiveness(res?.liveness)
                      setUserExists(res?.existingUser)
                      goNext()
                    }}
                    onError={handleError}
                  />,
                  <FaceMatch
                    key={`facematch-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={() => {
                      setIsShowDocV(false)
                    }}
                    liveness={liveness}
                    userExists={userExists}
                  />,
                  <RetrySteps
                    key={`retry-${step.toString()}`}
                    session={incodeSession}
                    numberOfTries={3}
                    onSuccess={goNext}
                    onError={handleError}
                  />,
                ].filter(Boolean)[step]
              }
            </>
          ) : null}
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? checkStatusError ?? isIncodeError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
